import React, { useEffect, useState, useCallback } from "react";
import {
    Box, Typography, Paper, CircularProgress, Container, Stack,
    FormControl, InputLabel, Select, MenuItem, Button, Table,
    TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
    DialogActions, TextField, Chip, Snackbar, Alert,
} from "@mui/material";
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
} from "@mui/icons-material";
import apiClient from "../api/apiClient";
import {
    getBundlePricing, createBundlePricing, updateBundlePricing,
    deleteBundlePricing, BundlePricing, BundlePricingPayload,
} from "../services/bundlePricingService";

const TENANT_ID = process.env.REACT_APP_TENANT_ID ?? "1";

interface SchoolClass { id: number; name: string; }

const emptyForm = { subjectCount: "", price: "" };

export default function BundlePricingPage(): React.ReactElement {
    const [classes, setClasses] = useState<SchoolClass[]>([]);
    const [selectedClassId, setSelectedClassId] = useState<number | "">("");
    const [pricings, setPricings] = useState<BundlePricing[]>([]);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState<{ type: "success" | "error"; msg: string } | null>(null);

    // Dialog state
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editTarget, setEditTarget] = useState<BundlePricing | null>(null);
    const [form, setForm] = useState(emptyForm);
    const [formErrors, setFormErrors] = useState<{ subjectCount?: string; price?: string }>({});
    const [saving, setSaving] = useState(false);

    // Load classes on mount
    useEffect(() => {
        apiClient.get("/classes", { params: { tenantId: TENANT_ID, size: 100 } })
            .then(r => {
                const data = r.data;
                const list = Array.isArray(data) ? data : (data?.content || []);
                setClasses(list);
                if (list.length > 0) setSelectedClassId(list[0].id);
            })
            .catch(() => setSnackbar({ type: "error", msg: "Failed to load classes" }));
    }, []);

    // Load pricing when class changes
    const fetchPricings = useCallback(async () => {
        if (!selectedClassId) return;
        setLoading(true);
        try {
            const data = await getBundlePricing(Number(selectedClassId));
            setPricings(data);
        } catch {
            setSnackbar({ type: "error", msg: "Failed to load pricing" });
        } finally {
            setLoading(false);
        }
    }, [selectedClassId]);

    useEffect(() => { fetchPricings(); }, [fetchPricings]);

    // Validation
    const validate = (): boolean => {
        const errs: { subjectCount?: string; price?: string } = {};
        const count = Number(form.subjectCount);
        const price = Number(form.price);
        if (!form.subjectCount || isNaN(count) || count < 1 || !Number.isInteger(count))
            errs.subjectCount = "Enter a valid integer ≥ 1";
        if (!form.price || isNaN(price) || price <= 0)
            errs.price = "Enter a valid price greater than 0";
        setFormErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const openAdd = () => {
        setEditTarget(null);
        setForm(emptyForm);
        setFormErrors({});
        setDialogOpen(true);
    };

    const openEdit = (row: BundlePricing) => {
        setEditTarget(row);
        setForm({ subjectCount: String(row.subjectCount), price: String(row.price) });
        setFormErrors({});
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!validate() || !selectedClassId) return;
        setSaving(true);
        const payload: BundlePricingPayload = {
            classId: Number(selectedClassId),
            subjectCount: Number(form.subjectCount),
            price: Number(form.price),
        };
        try {
            if (editTarget) {
                await updateBundlePricing(editTarget.id, payload);
                setSnackbar({ type: "success", msg: "Pricing updated" });
            } else {
                await createBundlePricing(payload);
                setSnackbar({ type: "success", msg: "Pricing added" });
            }
            setDialogOpen(false);
            fetchPricings();
        } catch (err: any) {
            const msg = err?.response?.data?.error || err.message || "Save failed";
            setSnackbar({ type: "error", msg });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm("Delete this pricing entry?")) return;
        try {
            await deleteBundlePricing(id);
            setSnackbar({ type: "success", msg: "Deleted" });
            fetchPricings();
        } catch (err: any) {
            setSnackbar({ type: "error", msg: err?.response?.data?.error || "Delete failed" });
        }
    };

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "#f5f7fa", py: 4 }}>
            <Container maxWidth="md">
                {/* Header */}
                <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Box>
                        <Typography variant="h4" fontWeight={700}>Bundle Pricing</Typography>
                        <Typography variant="body2" color="text.secondary" mt={0.5}>
                            Set subject bundle prices per class
                        </Typography>
                    </Box>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} disabled={!selectedClassId}>
                        Add Pricing
                    </Button>
                </Box>

                {/* Class selector */}
                <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 60 }}>Class:</Typography>
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                            <InputLabel>Select Class</InputLabel>
                            <Select
                                value={selectedClassId}
                                label="Select Class"
                                onChange={e => setSelectedClassId(Number(e.target.value))}
                            >
                                {classes.map(c => (
                                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {!loading && selectedClassId && (
                            <Chip label={`${pricings.length} tier${pricings.length !== 1 ? "s" : ""}`} size="small" color="primary" />
                        )}
                    </Stack>
                </Paper>

                {/* Pricing Table */}
                {loading ? (
                    <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
                ) : (
                    <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: "#f8fafc" }}>
                                        <TableCell sx={{ fontWeight: 700 }}>Subject Count</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Price (₹)</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pricings.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center" sx={{ py: 6, color: "text.secondary" }}>
                                                No pricing configured for this class. Click "Add Pricing" to start.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        pricings.map(row => (
                                            <TableRow key={row.id} hover>
                                                <TableCell>
                                                    <Chip
                                                        label={`${row.subjectCount} Subject${row.subjectCount !== 1 ? "s" : ""}`}
                                                        size="small"
                                                        sx={{ fontWeight: 600, bgcolor: "#e3f2fd", color: "#1565c0" }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography fontWeight={700} color="success.main">
                                                        ₹{Number(row.price).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                        <Tooltip title="Edit">
                                                            <IconButton size="small" onClick={() => openEdit(row)}>
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete">
                                                            <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                )}
            </Container>

            {/* Add / Edit Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle fontWeight={700}>{editTarget ? "Edit Pricing" : "Add Pricing"}</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2.5} mt={0.5}>
                        <TextField
                            label="Subject Count *"
                            type="number"
                            size="small"
                            fullWidth
                            value={form.subjectCount}
                            onChange={e => setForm(f => ({ ...f, subjectCount: e.target.value }))}
                            error={!!formErrors.subjectCount}
                            helperText={formErrors.subjectCount || "Number of subjects in this bundle (e.g. 1, 2, 3)"}
                            inputProps={{ min: 1, step: 1 }}
                        />
                        <TextField
                            label="Price (₹) *"
                            type="number"
                            size="small"
                            fullWidth
                            value={form.price}
                            onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                            error={!!formErrors.price}
                            helperText={formErrors.price || "Bundle price in INR (e.g. 2999)"}
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setDialogOpen(false)} color="inherit">Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                        {saving ? "Saving..." : editTarget ? "Update" : "Add"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!snackbar}
                autoHideDuration={4000}
                onClose={() => setSnackbar(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert onClose={() => setSnackbar(null)} severity={snackbar?.type || "info"}>
                    {snackbar?.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
}
